import { CustomException } from '@tencent/ppd-base-libs';
import { to } from '@tencent/wphk-base-utils';
import { getReturnActAward } from '@tencent/wphk-components-returning-user/business';
import { getInitCoupons, init } from '@tencent/wphk-components-returning-user/business/index';
import { getReturnCodeInfoFromCfg, formatMrchCfgByCode } from '@tencent/wphk-components-returning-user/business/merchantCfg';
import { ActState, returnCodeAct, type ReturnCodeAct } from '@tencent/wphk-components-returning-user/business/returnCodeAct';
import { getUserPkgCode } from '@tencent/wphk-components-returning-user/business/code-map';
import { queryCodeAndActMap } from '@tencent/wphk-components-returning-user/api/index';
import type { ReturnCodeCfg, SimpleCouponAwardCodeMap } from '@tencent/wphk-components-returning-user/types';

import { defineStore } from 'pinia';
import { toRefs } from 'vue';
import { useMerchantInfo } from './merchant-info-module';

/** 源活动模块 */
const initReturnCodeAct: ReturnCodeAct = returnCodeAct;
/** 回流活动商户信息 */
const initMrchConfig: ReturnCodeCfg = {} as any;
/** 回流券信息 */
const initRewardCoupon: SimpleCouponAwardCodeMap = {} as any;

export const useReturningUserStore = defineStore('returningUser', {
  state: () => ({
    returnCodeActInfo: initReturnCodeAct,
    mrchConfig: initMrchConfig,
    rewardCoupon: initRewardCoupon,
  }),
  getters: {
    canJoinAct(state) {
      const { actState } = state.returnCodeActInfo;
      return [ActState.UNRECEIVED, ActState.RECEIVING].includes(actState);
    },
    hasSelectedTargetAwardCode(state) {
      const { merchantId } = toRefs(useMerchantInfo());
      console.log('=====hasSelectedTargetAwardCode returnCodeAct actState', state.returnCodeActInfo.actState, state.returnCodeActInfo.awardCode);
      return [ActState.RECEIVED].includes(state.returnCodeActInfo.actState)
        && state.returnCodeActInfo.awardCode === state.mrchConfig.code
        // mrchId为空-通用券 || config里的id与url中merchantId相同
        && (!state.mrchConfig.mrchId || state.mrchConfig.mrchId === merchantId.value);
    },
  },
  actions: {
    /** 初始化活动 */
    async init() {
      const [err] = await to(init());
      const { merchantId } = toRefs(useMerchantInfo());
      if (err) {
        throw new CustomException(err, 'returningUserInitFail', '回流组件初始化失败');
      }

      const awardCode = this.returnCodeActInfo?.awardCode;
      console.log('====return codeAct', this.returnCodeActInfo.awardCode);
      // 注意：已上过线的回流码配置系统里不能删
      // 用户有参与资格，此时，拉取当前商户的回流码配置信息
      if (this.canJoinAct) {
        this.mrchConfig = await this.getReturnCodeMrchConfig(merchantId.value);
        console.log('====mrchConfig', this.mrchConfig);
        await this.getInitCoupon();
      } else if (awardCode) {
      // 用户没有参与资格，但是之前已经领过券了，此时，要展示已领券信息，因此，拉取该券对应的配置信息
        this.mrchConfig = await this.getUserReturnCodeMrchConfig(awardCode);
        console.log('====mrchConfig', this.mrchConfig);
        await this.getInitCoupon();
      }

      // 用户既没有参与资格，也没有曾经领过券，则直接不进入该模块
    },

    /**
     * 当用户作为回流用户时，有资格，获取此时的配置信息
     * @param merchantId
     * @returns
     */
    async getReturnCodeMrchConfig(merchantId) {
      // 如果需要展示本商户活动(unreceived/receiving)，那么要拉取对应mrchId的配置
      const originCfg = await getReturnCodeInfoFromCfg({ mrchId: merchantId });
      let awardCode = originCfg?.code;
      // 找出所属人群包，并根据人群包读取真正的code
      if (originCfg?.usrPkg?.length) {
        const codeMap = await queryCodeAndActMap();
        awardCode = await getUserPkgCode(originCfg, codeMap);
      }
      const formattedCfg = formatMrchCfgByCode(awardCode, originCfg);
      return formattedCfg;
    },

    /**
     * 获取已经参与过活动的配置信息
     */
    async getUserReturnCodeMrchConfig(awardCode) {
      // 如果已参与(received)，则拉取code对应的配置
      const awardOriginCfg = await getReturnCodeInfoFromCfg({ code: awardCode });
      const formattedCfg = formatMrchCfgByCode(awardCode, awardOriginCfg);
      return formattedCfg;
    },

    /** 获取回流券信息 */
    async getInitCoupon() {
      const targetAwardCode = !this.hasSelectedTargetAwardCode
        ? this.mrchConfig.code : this.returnCodeActInfo.awardCode;
      const [errCoupon, resCoupon] = await to(getInitCoupons({
        inputCode: targetAwardCode,
        returnCodeAct: this.returnCodeActInfo as ReturnCodeAct,
      }));
      if (errCoupon || !resCoupon) {
        console.log('======getInitCoupons error', errCoupon);
        throw errCoupon;
      }
      this.rewardCoupon = { awardCode: targetAwardCode, coupon: resCoupon };
      console.log('=====this.rewardCoupon', this.rewardCoupon);
    },

    /** 执行活动：领券 */
    async execAct(awardInfo: SimpleCouponAwardCodeMap) {
      const [err, res] = await to(getReturnActAward(awardInfo.awardCode));
      if (err) {
        new CustomException(err, 'ReturningUserExecActFail', '回流券领取失败');
        throw err;
      }
      return res;
    },
  },
});
